/**
 * A multispectral orthophoto: a vegetation index, the bands in false colour or one band at a
 * time, switched in a bar over the map, with the index's ramp and how far it is stretched;
 * an RGB orthophoto under it, the area's boundary and the elevation models (CHM, DSM, DTM) over it
 * when the report binds them. Below, the
 * bands the file holds, as the tiler found them by name.
 *
 * Indices are ratios, so they read the same whatever scale the reflectance is in; the bands
 * themselves are stretched over what the file holds (its 2nd to 98th percentile).
 */
import { useMemo, useState } from 'react';
import { Button, Group, Paper, RangeSlider, ScrollArea, SegmentedControl, Select, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/EmptyState';
import { useFormat } from '@/components/useFormat';
import { byKind, type LayerOptions, type ObjectKind } from '@/map/layers';
import { Scale } from '@/map/MapLegend';
import { MapView } from '@/map/MapView';
import { availableBands, availableDisplays, BAND_ROLES, indexStops, isIndex, type BandRole, type SpectralDisplay, type SpectralIndex, type SpectralView } from '@/map/spectral';
import { useRasterMeta, type RasterMeta } from '@/map/useRasterMeta';
import { brand } from '@/theme/theme';
import { MapFrame } from './MapFrame';
import { bandLabel } from './multispectral/bandLabel';
import { multispectralConfig, MULTISPECTRAL_SECTIONS, UNIT_DIGITS as DIGITS, valueUnit } from './multispectral/config';
import type { KindReportProps } from './ReportPage';
import { Section } from './trees/Section';

/** The elevation models over the bands, as the orthophoto report has them; they start switched off. */
const TOP_FIRST: ObjectKind[] = ['boundary', 'dsm', 'dtm', 'chm', 'multispectral_orthophoto', 'orthophoto'];
/** The index ramp's slider: an index is a ratio, between -1 and 1. */
const INDEX_MIN = -1;
const INDEX_MAX = 1;
const INDEX_STEP = 0.01;

export function MultispectralReport({ refs, linkSource, options, onOptionsChange, tokenSource, config, onVectorError, onVectorProgress }: KindReportProps) {
  const { t } = useTranslation('reports');
  const cfg = useMemo(() => multispectralConfig(config), [config]);
  const own = useMemo(() => byKind(refs, TOP_FIRST), [refs]);
  const ms = useMemo(() => refs.filter((r) => r.object_kind === 'multispectral_orthophoto' && r.via === 'tiler').slice(0, 1), [refs]);
  const { metas, isLoading } = useRasterMeta(ms, tokenSource);
  const meta = ms[0] ? metas[ms[0].object] : undefined;

  // What the viewer switched to; the report's own choice until they do. Not stored with the view.
  const [picked, setPicked] = useState<SpectralDisplay | null>(null);
  const [pickedBand, setPickedBand] = useState<BandRole | null>(null);
  const [stretches, setStretches] = useState<Partial<Record<SpectralIndex, [number, number]>>>({});

  const displays = availableDisplays(meta);
  const bands = availableBands(meta);
  const wanted = picked ?? cfg.display;
  // A file without the bands the report opens on still shows what it can.
  const display = !meta || displays.includes(wanted) ? wanted : (displays[0] ?? wanted);
  const wantedBand = pickedBand ?? cfg.band;
  const band = !meta || bands.includes(wantedBand) ? wantedBand : (bands[0] ?? wantedBand);
  const stretch = isIndex(display) ? (stretches[display] ?? cfg.stretch[display]) : undefined;
  const view: SpectralView = { display, band, stretch };

  const s = cfg.sections;
  if (!MULTISPECTRAL_SECTIONS.some((k) => s[k].enabled)) return <EmptyState title={t('multispectral.nothingEnabled')} />;
  if (!ms.length) return <EmptyState title={t('multispectral.unavailable')} />;

  // The view is the page's, not part of what a viewer's browser keeps.
  const change = (next: LayerOptions) => {
    const rest = { ...next };
    delete rest.spectral;
    onOptionsChange(rest);
  };
  const setStretch = (index: SpectralIndex, range: [number, number] | undefined) =>
    setStretches((prev) => {
      const next = { ...prev };
      if (range) next[index] = range;
      else delete next[index];
      return next;
    });

  return (
    <Stack gap="xl">
      {s.map.enabled && (
        <Stack gap="sm">
          <DisplayBar
            meta={meta}
            loading={isLoading}
            displays={meta ? displays : []}
            bands={bands}
            view={view}
            fileDefault={isIndex(display) ? meta?.index_ranges?.[display] : undefined}
            onDisplay={setPicked}
            onBand={setPickedBand}
            onStretch={(range) => isIndex(display) && setStretch(display, range)}
            onReset={() => isIndex(display) && setStretch(display, undefined)}
          />
          <MapFrame testId="report-multispectral" wide={!!options.wide} onWideChange={(wide) => change({ ...options, wide })}>
            <MapView
              refs={own}
              linkSource={linkSource}
              onVectorError={onVectorError}
              onVectorProgress={onVectorProgress}
              options={{ ...options, spectral: view }}
              onOptionsChange={change}
              tokenSource={tokenSource}
            />
          </MapFrame>
        </Stack>
      )}
      {s.bands.enabled && meta && <BandsSection meta={meta} />}
    </Stack>
  );
}

interface DisplayBarProps {
  meta: RasterMeta | undefined;
  loading: boolean;
  displays: SpectralDisplay[];
  bands: BandRole[];
  view: SpectralView;
  /** The index's 2nd to 98th percentile over the file, when the tiler measured it. */
  fileDefault: [number, number] | undefined;
  onDisplay: (display: SpectralDisplay) => void;
  onBand: (band: BandRole) => void;
  onStretch: (range: [number, number]) => void;
  onReset: () => void;
}

/** The switch between indices, false colour and single bands, and what the current one means. */
function DisplayBar({ meta, loading, displays, bands, view, fileDefault, onDisplay, onBand, onStretch, onReset }: DisplayBarProps) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  if (!meta) {
    return (
      <Text fz="sm" c={brand.textMuted} data-testid="ms-meta-pending">
        {loading ? t('multispectral.loading') : t('multispectral.noMeta')}
      </Text>
    );
  }
  if (!displays.length) return <EmptyState title={t('multispectral.noBands')} />;
  const bandIndex = meta.band_roles?.[view.band ?? 'nir'];
  const bandRange = bandIndex ? meta.value_range[bandIndex - 1] : undefined;
  return (
    <Paper withBorder p="sm" radius="md" data-testid="ms-display-bar">
      <Stack gap="sm">
        <Group gap="md" wrap="wrap" align="flex-end">
          <SegmentedControl
            value={view.display}
            onChange={(v) => onDisplay(v as SpectralDisplay)}
            data={displays.map((d) => ({ value: d, label: t(`multispectral.displays.${d}`) }))}
            data-testid="ms-display"
          />
          {view.display === 'band' && (
            <Select
              aria-label={t('multispectral.band')}
              value={view.band}
              onChange={(v) => v && onBand(v as BandRole)}
              allowDeselect={false}
              data={bands.map((b) => ({ value: b, label: bandLabel(t, meta, b) }))}
              w={220}
              data-testid="ms-band"
            />
          )}
        </Group>
        {isIndex(view.display) && view.stretch && (
          <IndexControls index={view.display} stretch={view.stretch} fileDefault={fileDefault} onStretch={onStretch} onReset={onReset} />
        )}
        {view.display === 'cir' && (
          <Text fz="sm" c={brand.textMuted} data-testid="ms-cir-hint">
            {t('multispectral.cirHint')}
          </Text>
        )}
        {view.display === 'band' && (
          <Text fz="sm" c={brand.textMuted} data-testid="ms-band-hint">
            {bandRange
              ? t('multispectral.bandHint', { low: number(bandRange[0], DIGITS[valueUnit(meta)]), high: number(bandRange[1], DIGITS[valueUnit(meta)]), unit: t(`multispectral.units.${valueUnit(meta)}`) })
              : t('multispectral.bandHintNoRange')}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function IndexControls({
  index,
  stretch,
  fileDefault,
  onStretch,
  onReset,
}: {
  index: SpectralIndex;
  stretch: [number, number];
  fileDefault: [number, number] | undefined;
  onStretch: (range: [number, number]) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  // The slider moves freely; the tiles are asked for again only once it is let go.
  const [dragging, setDragging] = useState<[number, number] | null>(null);
  const shown = dragging ?? stretch;
  return (
    <Stack gap="xs">
      <Scale entry={{ id: `index-${index}`, label: `layers.indices.${index}`, sample: { kind: 'ramp', colors: [] }, stops: indexStops(shown), hint: `layers.formulas.${index}` }} />
      <Group gap="md" wrap="wrap" align="center">
        <Text fz="sm" w={150}>
          {t('multispectral.stretch', { low: number(shown[0], 2), high: number(shown[1], 2) })}
        </Text>
        <RangeSlider
          min={INDEX_MIN}
          max={INDEX_MAX}
          step={INDEX_STEP}
          minRange={INDEX_STEP}
          value={shown}
          onChange={(v) => setDragging(v)}
          onChangeEnd={(v) => {
            setDragging(null);
            onStretch(v);
          }}
          label={(v) => number(v, 2)}
          style={{ flex: 1, minWidth: 200 }}
          aria-label={t('multispectral.stretchLabel')}
          data-testid="ms-stretch"
        />
        <Button variant="subtle" size="xs" onClick={onReset} data-testid="ms-stretch-reset">
          {t('multispectral.stretchReset')}
        </Button>
        {fileDefault && (
          <Button variant="subtle" size="xs" onClick={() => onStretch([round2(fileDefault[0]), round2(fileDefault[1])])} data-testid="ms-stretch-file">
            {t('multispectral.stretchFile')}
          </Button>
        )}
      </Group>
    </Stack>
  );
}

const round2 = (v: number) => Math.round(v * 100) / 100;


/** The file's bands as the tiler read them: name, what it was taken for, and the values it spans. */
function BandsSection({ meta }: { meta: RasterMeta }) {
  const { t } = useTranslation('reports');
  const { t: tv } = useTranslation('viewer');
  const { number } = useFormat();
  const roleOf = new Map(Object.entries(meta.band_roles ?? {}).map(([role, i]) => [i, role as BandRole]));
  const rows = Array.from({ length: meta.bands }, (_, i) => i + 1);
  const unit = valueUnit(meta);
  // The coding of the reflectance bands (alpha keeps 1), as the file states it.
  const scale = Object.values(meta.band_roles ?? {})
    .map((b) => meta.scales?.[b - 1])
    .find((v) => v !== undefined && v !== 1);
  return (
    <Section title={t('multispectral.sections.bands')}>
      <Stack gap="sm">
        <Text fz="sm" c={brand.textMuted} data-testid="ms-file">
          {t('multispectral.file', { resolution: number(meta.resolution_m * 100, 1), dtype: meta.dtype, count: meta.bands })}
          {meta.unscaled && scale !== undefined && ` · ${t('multispectral.scaled', { scale: number(scale, 6) })}`}
          {meta.warnings.length > 0 && ` · ${meta.warnings.map((w) => tv(`layers.warnings.${w}`, { defaultValue: w })).join(', ')}`}
        </Text>
        <ScrollArea type="auto">
          <Table striped withTableBorder data-testid="ms-bands">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('multispectral.columns.index')}</Table.Th>
                <Table.Th>{t('multispectral.columns.name')}</Table.Th>
                <Table.Th>{t('multispectral.columns.role')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('multispectral.columns.range', { unit: t(`multispectral.units.${unit}`) })}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((i) => {
                const role = roleOf.get(i);
                const range = meta.value_range[i - 1];
                return (
                  <Table.Tr key={i} data-testid={`ms-band-row-${i}`}>
                    <Table.Td>{i}</Table.Td>
                    <Table.Td>{meta.band_names?.[i - 1] ?? '—'}</Table.Td>
                    <Table.Td>{role && (BAND_ROLES as readonly string[]).includes(role) ? t(`multispectral.bands.${role}`) : '—'}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{range ? `${number(range[0], DIGITS[unit])} – ${number(range[1], DIGITS[unit])}` : '—'}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <Text fz="xs" c={brand.textMuted}>
          {t('multispectral.calibrationNote')}
        </Text>
      </Stack>
    </Section>
  );
}
